// Item kinds: "video" | "driveFolder" | "driveFile" | "driveDownload"
//           | "external" | "route"

export const driveFolderUrl = (id) =>
  `https://drive.google.com/drive/folders/${id}?usp=sharing`;

export const driveFileUrl = (id) =>
  `https://drive.google.com/file/d/${id}/view?usp=sharing`;

// Downloads the file instead of opening the Drive viewer. The file must be
// shared "Anyone with the link" and stay under 100MB - past that Google serves
// a "can't scan for viruses" page instead of the file.
export const driveDownloadUrl = (id) =>
  `https://drive.google.com/uc?export=download&id=${id}`;

// Page-one preview PNG. Files only - this 404s for a folder id - and the file
// must be shared "Anyone with the link".
export const driveThumbnailUrl = (id, width = 800) =>
  `https://drive.google.com/thumbnail?id=${id}&sz=w${width}`;

export const youTubeWatchUrl = (id, startSeconds) =>
  `https://www.youtube.com/watch?v=${id}${startSeconds ? `&t=${startSeconds}s` : ""}`;

export const DRIVE_IDS = {
  resourcesLibrary: "1HpXM89eDyK5LIIW8MgumAzeMyadBzg-D",
  postersFolder: "1GliXPICvxdfJrqu2gXOHLNIfMl2pR8KO",
  teenPoster: "1K5SGg_MlhPP3E8bFTzbXdMcYO8xMF8IZ",
  pamphlet: "1l2neYJ9lhUxxAmHwlXmGFwuzAfqmS6m2",
  introPowerPoint: "1JiG60bQr7qwIJgh54IFS4DuUpuQlIQjw",
  workbookElementary: "1Jd2_Y69enLWtPqQcj4tyGxSzyqXOhSJb",
  workbookMiddle: "1fobTLmOdEDCueHf1gGjgKCsuuhMqaQPL",
  workbookHigh: "1d-5gbn0tC45kr2iubpHJ-bYibmkeh-22",
};

const INTRO_VIDEO_ELEMENTARY = {
  id: "intro-video-elementary",
  kind: "video",
  youTubeId: "76ZqNLxZAwk",
  startSeconds: 0,
  posterSrc: "/images/resources/ElementarySchoolKidsImage.jpeg",
  title: "Elementary Intro Video",
};

const INTRO_VIDEO_SECONDARY = {
  id: "intro-video-secondary",
  kind: "video",
  youTubeId: "WX0hu4wzZ10",
  startSeconds: 2,
  posterSrc: "/images/resources/MiddleAndHighSchoolKidsImage.jpeg",
  title: "Middle & High School Intro Video",
};

/* ------------------------------ Educator ------------------------------ */

export const EDUCATOR_HERO = {
  heading: "Congratulations on building a wellness-fueled classroom!",
  body: "Resources are here to support YOU in making sure that every student has healthy outlets, every day. To help achieve this goal, please create a daily routine so that every student uses the 1-2-3 Wellness app once a day.",
  learnMore: {
    label: "Why daily use matters",
    paragraphs: [
      {
        lead: "Daily Wellness",
        text: "Many students come to school “stuck” in their brainstems and this can affect their learning, behavior and more. When students use the app daily, their ability to learn, pay attention and succeed grows.",
      },
      {
        lead: "Powerful Data",
        text: "In addition to building daily habits, you and your students will get powerful data once students check-in using the app daily. The “My Story” tab helps your students learn their personal needs.",
      },
      "The “Educator Dashboard” and “Student Supports” tabs reveal vital data about your students’ emotions and needs, MTSS insights and how you can help your students thrive.",
    ],
  },
};

export const SELF_CARE_CHALLENGE_SECTION = {
  id: "self-care-challenge",
  title: "4. Explore Adult Wellness Resources for YOU",
  description:
    "Participate in the 21-day Self-care Challenge designed to support adult wellness.",
  body: "The 21-Day Self-Care Challenge consists of 21 videos, about five minutes each, that guide you to build your own self-care plan.",
  ctaLabel: "Start Challenge",
  to: "/selfcarechallenge",
};

export const EDUCATOR_SECTIONS = [
  {
    id: "start-here",
    title: "1. Start Here: Watch an Intro. Video",
    compact: true,
    description:
      "Start with a short intro video to help you and your students learn about 1-2-3 Wellness.",
    items: [
      {
        ...INTRO_VIDEO_ELEMENTARY,
        description:
          "A short overview of 1-2-3 Wellness to watch with your elementary students.",
      },
      {
        ...INTRO_VIDEO_SECONDARY,
        description:
          "A short overview of 1-2-3 Wellness to watch with your secondary students.",
      },
    ],
  },
  {
    id: "learn-more",
    compact: true,
    title: "2. Learn More About 1-2-3 Wellness",
    items: [
      {
        id: "website",
        kind: "external",
        url: "https://www.123wellness.org/",
        title: "1-2-3 Wellness Website",
        previewSrc: "/images/resources/123wellness-website.webp",
        previewAlt: "Students stretching together during a 1-2-3 Wellness activity",
        description: "Background, research and more about 1-2-3 Wellness.",
        ctaLabel: "Visit 123wellness.org",
      },
      {
        id: "pamphlet",
        kind: "driveDownload",
        fileKind: "pdf",
        driveId: DRIVE_IDS.pamphlet,
        title: "1-2-3 Wellness Pamphlet",
        previewSrc: driveThumbnailUrl(DRIVE_IDS.pamphlet),
        description:
          "This pamphlet includes resources to help make your experience smooth and powerful.",
      },
      {
        id: "intro-powerpoint",
        kind: "driveDownload",
        fileKind: "pdf",
        driveId: DRIVE_IDS.introPowerPoint,
        title: "Introductory PowerPoint",
        previewSrc: driveThumbnailUrl(DRIVE_IDS.introPowerPoint),
        description:
          "Slides for introducing 1-2-3 Wellness to your school.",
      },
      {
        id: "teen-poster",
        kind: "driveDownload",
        fileKind: "pdf",
        driveId: DRIVE_IDS.teenPoster,
        title: "1-2-3 Wellness Posters",
        previewSrc: driveThumbnailUrl(DRIVE_IDS.teenPoster),
        description:
          "Please download and print a poster for your classroom wall.",
        secondary: {
          label: "For more posters, click here",
          url: driveFolderUrl(DRIVE_IDS.postersFolder),
        },
      },
    ],
  },
  {
    id: "resources-library",
    title: "3. See the Full Resources Library",
    description:
      "Lesson plans, student workbooks and more, all in one Drive folder.",
    items: [
      {
        id: "resources-library-folder",
        kind: "driveFolder",
        driveId: DRIVE_IDS.resourcesLibrary,
        title: "Resources Library",
        description:
          "To access lesson plans please click the “Educator Guide” document in the Drive.",
      },
    ],
  },
];

export const EDUCATOR_BRAIN_SECTION = {
  id: "brain-model",
  title: "5. How 1-2-3 Wellness Helps Your Brain",
  description:
    "The three steps — Regulate, Relate and Reason — and what each one does. Worth sharing with your students.",
};

export const EDUCATOR_FOOTER_NOTE =
  "These resources, paired with your app data, help you make powerful, data-based decisions for your students. Keep up the great work!";

/* ------------------------------ Student ------------------------------- */

export const STUDENT_HERO = {
  heading: "Congratulations on taking steps to build your wellness!",
  body: "Each time you use the 1-2-3 Wellness app, you are building your wellness skills. Nice job! Watch a short intro video to learn more. Next, download your workbook.",
};

export const STUDENT_SECTIONS = [
  {
    id: "start-here",
    title: "1. Start Here: Watch an Intro. Video",
    compact: true,
    items: [
      {
        ...INTRO_VIDEO_ELEMENTARY,
        description: "A short video for elementary students.",
      },
      {
        ...INTRO_VIDEO_SECONDARY,
        description: "A short video for middle and high school students.",
      },
    ],
  },
  {
    id: "workbook",
    compact: true,
    title: "2. Download and Save Your Workbook",
    description:
      "Your 1-2-3 Wellness workbook will help you build even more healthy habits. Download yours now to get started!",
    learnMore: {
      label: "How to save your workbook",
      paragraphs: [
        "Click download and your workbook is saved straight to your device. Open it from your downloads, then save it again once you have written in it so your answers are kept. Enjoy!",
      ],
    },
    items: [
      {
        id: "workbook-elementary",
        kind: "driveDownload",
        fileKind: "pdf",
        driveId: DRIVE_IDS.workbookElementary,
        title: "Elementary School Students",
        previewSrc: driveThumbnailUrl(DRIVE_IDS.workbookElementary),
        description: "Your 1-2-3 Wellness workbook for elementary school.",
      },
      {
        id: "workbook-middle",
        kind: "driveDownload",
        fileKind: "pdf",
        driveId: DRIVE_IDS.workbookMiddle,
        title: "Middle School Students",
        previewSrc: driveThumbnailUrl(DRIVE_IDS.workbookMiddle),
        description: "Your 1-2-3 Wellness workbook for middle school.",
      },
      {
        id: "workbook-high",
        kind: "driveDownload",
        fileKind: "pdf",
        driveId: DRIVE_IDS.workbookHigh,
        title: "High School Students",
        previewSrc: driveThumbnailUrl(DRIVE_IDS.workbookHigh),
        description: "Your 1-2-3 Wellness workbook for high school.",
      },
    ],
  },
];

export const STUDENT_BRAIN_SECTION = {
  id: "brain-model",
  title: "3. How 1-2-3 Wellness Helps Your Brain",
  description:
    "Each time you practice 1-2-3 Wellness, you are helping your brain to learn and grow!",
};

export const STUDENT_FOOTER_NOTE =
  "Remember to save a copy of your workbook to your computer — and have fun!";
